/**
 * Route manifest: single source of truth for the application's URL surface.
 *
 * `ROUTE_PATTERNS` mirrors every `path=` registered in `src/App.tsx`
 * (the `*` NotFound wildcard is intentionally excluded).
 * `src/routes/routeManifest.test.ts` fails CI if App.tsx and this list drift
 * apart, so a new route cannot be added without a matching policy entry.
 */

/** Routes that are intentionally reachable without authentication. */
export const PUBLIC_EXACT_PATHS: ReadonlySet<string> = new Set([
  // Marketing & public surfaces
  '/',
  '/about',
  '/api-reference',
  '/architecture',
  '/community',
  '/contact',
  '/data-insights',
  '/docs',
  '/features',
  '/for-chambers',
  '/for-governments',
  '/for-members',
  '/membership',
  '/partners',
  '/press',
  '/pricing',
  '/privacy',
  '/roadmap',
  '/security',
  '/signup',
  '/terms',
  '/trade-trust',
  '/verify-co',
])

/** Every route pattern registered in src/App.tsx (params as `:name`). */
export const ROUTE_PATTERNS: readonly string[] = [
  '/',
  '/about',
  '/advocacy',
  '/advocacy/brief-drafter',
  '/advocacy/issues/:id',
  '/advocacy/poll-create',
  '/analytics',
  '/analytics/catalog',
  '/analytics/data-engineering',
  '/analytics/indicators',
  '/api-reference',
  '/architecture',
  '/billing',
  '/billing/invoice',
  '/billing/plans',
  '/billing/usage',
  '/community',
  '/concierge',
  '/concierge/matchmaking',
  '/contact',
  '/dashboard',
  '/data-insights',
  '/data-room',
  '/directory',
  '/docs',
  '/email-templates',
  '/events',
  '/events/:id',
  '/events/checkin',
  '/events/create',
  '/events/expo',
  '/events/transfer',
  '/features',
  '/for-chambers',
  '/for-governments',
  '/for-members',
  '/grants',
  '/grants/:id',
  '/grants/apply',
  '/import',
  '/jobs',
  '/jobs/:id',
  '/marketplace',
  '/marketplace/disputes/:id',
  '/marketplace/rfp-create',
  '/marketplace/rfp/:id',
  '/marketplace/vendor-apply',
  '/marketplace/vendors/:id',
  '/members',
  '/members/:id',
  '/membership',
  '/moderation',
  '/notifications',
  '/onboarding',
  '/partner-portal',
  '/partners',
  '/people/:id',
  '/press',
  '/pricing',
  '/privacy',
  '/profile',
  '/reports',
  '/roadmap',
  '/search',
  '/security',
  '/settings',
  '/settings/api-keys',
  '/settings/api-keys/:id',
  '/settings/audit',
  '/settings/dba',
  '/settings/dba/queries/:queryId',
  '/settings/dba/tables/:name',
  '/settings/incidents',
  '/settings/security',
  '/settings/security/pii/:tableName/:columnName',
  '/settings/security/roles/:id',
  '/settings/sre',
  '/settings/sre/canary/:id',
  '/settings/sre/slo/:id',
  '/signup',
  '/sponsors',
  '/sponsors/:id',
  '/terms',
  '/trade',
  '/trade-trust',
  '/trade/co-application',
  '/training',
  '/training/:id',
  '/verify-co',
  '/webhooks',
]

/** Match a concrete pathname against a manifest pattern (`:param` wildcards). */
export function matchesRoutePattern(pathname: string, pattern: string): boolean {
  if (pattern === pathname) return true
  const pathSegments = pathname.split('/')
  const patternSegments = pattern.split('/')
  if (pathSegments.length !== patternSegments.length) return false
  return patternSegments.every((segment, index) =>
    segment.startsWith(':') ? pathSegments[index].length > 0 : segment === pathSegments[index],
  )
}

/** True when the pathname corresponds to a route registered in the manifest. */
export function isKnownRoute(pathname: string): boolean {
  return ROUTE_PATTERNS.some((pattern) => matchesRoutePattern(pathname, pattern))
}
